import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  FileText,
  Search,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  MessageSquare,
  Paperclip,
  ChevronRight,
  Calendar,
  Building2,
  Eye,
  ThumbsUp,
  ThumbsDown,
  X,
  Send,
  Download,
  History,
  User,
  Loader2,
  ArrowLeftRight,
  Scale,
  Edit3,
  Check,
  Upload,
  Trash2
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useAuth, ROLES } from '../context/AuthContext';
import { useLoans, useWaivers, useWaiverStats, useWaiverMutations } from '../hooks';


const STATUS_CONFIG = {
  draft: {
    label: 'Draft',
    color: 'bg-slate-100 text-slate-700',
    icon: FileText
  },
  pending_review: {
    label: 'Pending Review',
    color: 'bg-amber-100 text-amber-700',
    icon: Clock
  },
  negotiating: {
    label: 'Negotiating',
    color: 'bg-purple-100 text-purple-700',
    icon: ArrowLeftRight
  },
  under_voting: {
    label: 'Under Voting',
    color: 'bg-blue-100 text-blue-700',
    icon: Users
  },
  approved: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700',
    icon: CheckCircle
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700',
    icon: XCircle
  },
  expired: {
    label: 'Expired',
    color: 'bg-slate-100 text-slate-500',
    icon: AlertTriangle
  },
  withdrawn: {
    label: 'Withdrawn',
    color: 'bg-slate-100 text-slate-500',
    icon: XCircle
  }
};


const WaiverCard = ({ waiver, onView, onVote, userRole }) => {
  const { t } = useTranslation();
  const statusConfig = STATUS_CONFIG[waiver.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  const progressColor = waiver.currentApproval >= waiver.requiredMajority
    ? 'bg-green-500'
    : waiver.currentApproval > 0
      ? 'bg-blue-500'
      : 'bg-slate-200';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      {}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
            <StatusIcon size={20} className={statusConfig.color.split(' ')[1]} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{waiver.id}</h3>
            <p className="text-sm text-slate-500">{waiver.borrower}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
          {t(`waivers.status.${waiver.status}`) || statusConfig.label}
        </span>
      </div>

      {}
      <div className="bg-slate-50 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <span className="text-sm font-medium text-slate-700 truncate max-w-[60%]">{waiver.covenantName}</span>
          <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded whitespace-nowrap">
            {waiver.covenantType}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 text-sm flex-wrap">
          <div className="flex items-center">
            <span className="text-slate-500 text-xs sm:text-sm">{t('waivers.current')}:</span>
            <span className="ml-1 font-medium text-red-600">{waiver.currentValue}</span>
          </div>
          <ChevronRight size={16} className="text-slate-400 hidden sm:block" />
          <div className="flex items-center">
            <span className="text-slate-500 text-xs sm:text-sm">{t('waivers.threshold')}:</span>
            <span className="ml-1 font-medium text-slate-700">{waiver.threshold}</span>
          </div>
        </div>
      </div>

      {}
      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{waiver.requestedWaiver}</p>

      {}
      {waiver.status === 'under_voting' && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-600">
              {t('waivers.votingProgress')}: {waiver.votesReceived || 0}/{waiver.totalLenders || 0}
            </span>
            <span className={`font-medium ${(waiver.currentApproval || 0) >= (waiver.requiredMajority || 66.67) ? 'text-green-600' : 'text-blue-600'}`}>
              {(waiver.currentApproval || 0).toFixed(1)}% {t('waivers.approval')}
            </span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${progressColor} transition-all`}
              style={{ width: `${waiver.currentApproval || 0}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
            <span>{t('waivers.requiredMajority')}: {waiver.requiredMajority || 66.67}%</span>
            {waiver.votingDeadline && (
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {t('waivers.deadline')}: {new Date(waiver.votingDeadline).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}

      {}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <MessageSquare size={14} />
            {waiver.comments?.length || 0}
          </span>
          <span className="flex items-center gap-1">
            <Paperclip size={14} />
            {waiver.documents?.length || 0}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {waiver.status === 'under_voting' && userRole === ROLES.LENDER && (
            <>
              <button
                onClick={() => onVote(waiver.dbId, 'approve')}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title={t('waivers.approve')}
              >
                <ThumbsUp size={18} />
              </button>
              <button
                onClick={() => onVote(waiver.dbId, 'reject')}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t('waivers.reject')}
              >
                <ThumbsDown size={18} />
              </button>
            </>
          )}
          <button
            onClick={() => onView(waiver)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Eye size={16} />
            {t('common.view')}
          </button>
        </div>
      </div>
    </div>
  );
};


const NewWaiverModal = ({ isOpen, onClose, loans, onSubmit, isSubmitting }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    loanId: '',
    covenantName: '',
    covenantType: 'financial',
    currentValue: '',
    threshold: '',
    requestedWaiver: '',
    reason: '',
    requiredMajority: 66.67
  });
  const [documents, setDocuments] = useState([]);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleSubmit = async (e, asDraft = false) => {
    e.preventDefault();
    await onSubmit({ ...formData, status: asDraft ? 'draft' : 'pending_review' }, documents);

    setFormData({
      loanId: '',
      covenantName: '',
      covenantType: 'financial',
      currentValue: '',
      threshold: '',
      requestedWaiver: '',
      reason: '',
      requiredMajority: 66.67
    });
    setDocuments([]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setDocuments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))]);
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto m-4">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-800">{t('waivers.newRequest')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)} className="p-6 space-y-6">
          {}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('waivers.selectLoan')} *
            </label>
            <select
              value={formData.loanId}
              onChange={(e) => {
                console.log('Selected loan value:', e.target.value, 'type:', typeof e.target.value);
                setFormData({ ...formData, loanId: e.target.value });
              }}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('waivers.selectLoanPlaceholder')}</option>
              {loans.map(loan => {
                console.log('Loan option:', { id: loan.id, dbId: loan.dbId, reference: loan.reference, borrower: loan.borrower });
                return (
                  <option key={loan.id} value={loan.id}>
                    {loan.borrower || loan.borrower_name || 'Unknown'} - {loan.reference || loan.id}
                  </option>
                );
              })}
            </select>
          </div>

          {}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('waivers.covenantName')} *
              </label>
              <input
                type="text"
                value={formData.covenantName}
                onChange={(e) => setFormData({ ...formData, covenantName: e.target.value })}
                placeholder="e.g., Leverage Ratio"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('waivers.covenantType')} *
              </label>
              <select
                value={formData.covenantType}
                onChange={(e) => setFormData({ ...formData, covenantType: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="financial">{t('waivers.types.financial')}</option>
                <option value="operational">{t('waivers.types.operational')}</option>
                <option value="reporting">{t('waivers.types.reporting')}</option>
                <option value="other">{t('waivers.types.other')}</option>
              </select>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('waivers.currentValue')} *
              </label>
              <input
                type="text"
                value={formData.currentValue}
                onChange={(e) => setFormData({ ...formData, currentValue: e.target.value })}
                placeholder="e.g., 4.8x"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('waivers.thresholdValue')} *
              </label>
              <input
                type="text"
                value={formData.threshold}
                onChange={(e) => setFormData({ ...formData, threshold: e.target.value })}
                placeholder="e.g., 4.5x"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('waivers.requestedWaiver')} *
            </label>
            <input
              type="text"
              value={formData.requestedWaiver}
              onChange={(e) => setFormData({ ...formData, requestedWaiver: e.target.value })}
              placeholder="e.g., Temporary increase to 5.0x for Q1 2025"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('waivers.justification')} *
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder={t('waivers.justificationPlaceholder')}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('waivers.requiredMajority')} (%)
            </label>
            <select
              value={formData.requiredMajority}
              onChange={(e) => setFormData({ ...formData, requiredMajority: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={51}>{t('waivers.simpleMajority')} (51%)</option>
              <option value={66.67}>{t('waivers.superMajority')} (66.67%)</option>
              <option value={75}>{t('waivers.qualifiedMajority')} (75%)</option>
              <option value={100}>{t('waivers.unanimity')} (100%)</option>
            </select>
          </div>

          {}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('waivers.supportingDocuments')}
            </label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
            >
              <Upload size={24} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">{t('waivers.dragDropFiles')}</p>
              <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLS (max 10MB)</p>
            </div>
            {documents.length > 0 && (
              <div className="mt-3 space-y-2">
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-slate-500" />
                      <span className="text-sm text-slate-700">{doc.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              className="px-4 py-2 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50"
              disabled={isSubmitting}
            >
              {t('waivers.saveDraft')}
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {t('waivers.submitForReview')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const NegotiationSection = ({ negotiations, userRole, onMakeCounterProposal, onAcceptProposal, onStartNegotiation, waiverStatus }) => {
  const { t, language } = useTranslation();
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [showInitialForm, setShowInitialForm] = useState(false);
  const [counterProposal, setCounterProposal] = useState({ proposal: '', conditions: [''] });
  const [initialProposal, setInitialProposal] = useState({ proposal: '', conditions: [''] });

  const getProposalColor = (fromRole, status) => {
    if (status === 'active') {
      return fromRole === 'borrower'
        ? 'border-l-4 border-l-green-500 bg-green-50'
        : 'border-l-4 border-l-blue-500 bg-blue-50';
    }
    if (status === 'superseded') {
      return 'border-l-4 border-l-slate-300 bg-slate-50 opacity-60';
    }
    return fromRole === 'borrower'
      ? 'border-l-4 border-l-green-300 bg-green-50/50'
      : 'border-l-4 border-l-blue-300 bg-blue-50/50';
  };

  const addCondition = () => {
    setCounterProposal(prev => ({
      ...prev,
      conditions: [...prev.conditions, '']
    }));
  };

  const updateCondition = (index, value) => {
    setCounterProposal(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? value : c)
    }));
  };

  const removeCondition = (index) => {
    setCounterProposal(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitCounter = () => {
    if (counterProposal.proposal.trim()) {
      onMakeCounterProposal(counterProposal);
      setCounterProposal({ proposal: '', conditions: [''] });
      setShowCounterForm(false);
    }
  };

  const activeProposal = negotiations?.find(n => n.status === 'active');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <ArrowLeftRight size={18} className="text-purple-600" />
          {t('waivers.negotiation.title')}
        </h3>
        {activeProposal && (
          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
            {t('waivers.negotiation.round')} {negotiations?.length || 0}
          </span>
        )}
      </div>

      {}
      <div className="space-y-3">
        {negotiations?.map((neg, index) => (
          <div
            key={neg.id}
            className={`rounded-lg p-4 ${getProposalColor(neg.fromRole, neg.status)}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  neg.fromRole === 'borrower' ? 'bg-green-200' : 'bg-blue-200'
                }`}>
                  {neg.fromRole === 'borrower' ? (
                    <Building2 size={14} className="text-green-700" />
                  ) : (
                    <Scale size={14} className="text-blue-700" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{neg.from}</p>
                  <p className="text-xs text-slate-500">
                    {neg.type === 'initial' ? t('waivers.negotiation.initialProposal') : t('waivers.negotiation.counterProposal')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">
                  {new Date(neg.date).toLocaleDateString()}
                </span>
                {neg.status === 'active' && (
                  <span className="text-xs px-2 py-0.5 bg-purple-500 text-white rounded-full">
                    {t('waivers.negotiation.current')}
                  </span>
                )}
                {neg.status === 'superseded' && (
                  <span className="text-xs px-2 py-0.5 bg-slate-400 text-white rounded-full line-through">
                    {t('waivers.negotiation.superseded')}
                  </span>
                )}
              </div>
            </div>

            {}
            <div className="mb-3">
              <p className="text-sm font-medium text-slate-700">{neg.proposal}</p>
            </div>

            {}
            {neg.conditions && neg.conditions.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-slate-500 uppercase">{t('waivers.negotiation.conditions')}:</p>
                <ul className="space-y-1">
                  {neg.conditions.map((condition, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check size={14} className="mt-0.5 text-slate-400 flex-shrink-0" />
                      {condition}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {}
            {neg.status === 'active' && index === negotiations.length - 1 && (
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-200">
                {((neg.fromRole === 'borrower' && userRole === ROLES.LENDER) ||
                  (neg.fromRole === 'lender' && userRole === ROLES.BORROWER) ||
                  userRole === ROLES.AGENT) && (
                  <>
                    <button
                      onClick={() => onAcceptProposal(neg.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                    >
                      <Check size={14} />
                      {t('waivers.negotiation.accept')}
                    </button>
                    <button
                      onClick={() => setShowCounterForm(true)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                    >
                      <Edit3 size={14} />
                      {t('waivers.negotiation.counter')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {}
      {showCounterForm && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-4">
          <h4 className="font-medium text-purple-800 flex items-center gap-2">
            <Edit3 size={16} />
            {t('waivers.negotiation.makeCounter')}
          </h4>

          <div>
            <label className="block text-sm text-slate-700 mb-1">{t('waivers.negotiation.yourProposal')}</label>
            <textarea
              value={counterProposal.proposal}
              onChange={(e) => setCounterProposal(prev => ({ ...prev, proposal: e.target.value }))}
              placeholder={t('waivers.negotiation.proposalPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-700 mb-1">{t('waivers.negotiation.conditions')}</label>
            <div className="space-y-2">
              {counterProposal.conditions.map((condition, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={condition}
                    onChange={(e) => updateCondition(idx, e.target.value)}
                    placeholder={`${t('waivers.negotiation.condition')} ${idx + 1}`}
                    className="flex-1 px-3 py-1.5 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  {counterProposal.conditions.length > 1 && (
                    <button
                      onClick={() => removeCondition(idx)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addCondition}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                + {t('waivers.negotiation.addCondition')}
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => setShowCounterForm(false)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSubmitCounter}
              disabled={!counterProposal.proposal.trim()}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
            >
              {t('waivers.negotiation.submitCounter')}
            </button>
          </div>
        </div>
      )}

      {}
      {userRole === ROLES.AGENT && activeProposal && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-700 mb-3">{t('waivers.negotiation.agentMessage')}</p>
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Users size={16} />
            {t('waivers.negotiation.finalizeAndVote')}
          </button>
        </div>
      )}
    </div>
  );
};


const WaiverDetailModal = ({
  waiver,
  isOpen,
  onClose,
  userRole,
  user,
  onVote,
  onApproveForVoting,
  onAddComment,
  onMakeCounterProposal,
  onAcceptProposal
}) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !waiver) return null;

  const statusConfig = STATUS_CONFIG[waiver.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  const handleAddComment = async () => {
    if (newComment.trim()) {
      setIsSubmitting(true);
      await onAddComment(waiver.dbId, newComment);
      setNewComment('');
      setIsSubmitting(false);
    }
  };

  const hasNegotiations = waiver.negotiations && waiver.negotiations.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
        {}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
              <StatusIcon size={20} className={statusConfig.color.split(' ')[1]} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{waiver.id}</h2>
              <p className="text-sm text-slate-500">{waiver.borrower}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
              {t(`waivers.status.${waiver.status}`) || statusConfig.label}
            </span>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        {}
        {(waiver.status === 'negotiating' || hasNegotiations) && (
          <div className="px-6 pt-4 border-b border-slate-200">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('details')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('waivers.tabs.details')}
              </button>
              <button
                onClick={() => setActiveTab('negotiation')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'negotiation'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <ArrowLeftRight size={16} />
                {t('waivers.tabs.negotiation')}
                {hasNegotiations && (
                  <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                    {waiver.negotiations.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="p-6">
          {}
          {activeTab === 'negotiation' && hasNegotiations ? (
            <NegotiationSection
              negotiations={waiver.negotiations}
              userRole={userRole}
              onMakeCounterProposal={onMakeCounterProposal}
              onAcceptProposal={onAcceptProposal}
            />
          ) : (
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {}
              <div className="lg:col-span-2 space-y-6">
                {}
                <div className="bg-slate-50 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-800 mb-4">{t('waivers.covenantDetails')}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">{t('waivers.covenantName')}</p>
                      <p className="font-medium text-slate-800">{waiver.covenantName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t('waivers.covenantType')}</p>
                      <p className="font-medium text-slate-800 capitalize">{waiver.covenantType}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t('waivers.currentValue')}</p>
                      <p className="font-medium text-red-600">{waiver.currentValue}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t('waivers.threshold')}</p>
                      <p className="font-medium text-slate-800">{waiver.threshold}</p>
                    </div>
                  </div>
                </div>

                {}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">{t('waivers.requestedWaiver')}</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 font-medium">{waiver.requestedWaiver}</p>
                  </div>
                </div>

                {}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">{t('waivers.justification')}</h3>
                  <p className="text-slate-600">{waiver.reason}</p>
                </div>

                {}
                {waiver.documents && waiver.documents.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-3">{t('waivers.supportingDocuments')}</h3>
                    <div className="space-y-2">
                      {waiver.documents.map((doc, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FileText size={18} className="text-slate-500" />
                            <span className="text-sm text-slate-700">{typeof doc === 'string' ? doc : doc.name}</span>
                          </div>
                          <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <Download size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">
                    {t('waivers.comments')} ({waiver.comments?.length || 0})
                  </h3>
                  <div className="space-y-3 mb-4">
                    {waiver.comments && waiver.comments.length > 0 ? (
                      waiver.comments.map(comment => (
                        <div key={comment.id} className="bg-slate-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User size={14} className="text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{comment.user}</p>
                                <p className="text-xs text-slate-500">{comment.role}</p>
                              </div>
                            </div>
                            <span className="text-xs text-slate-400">
                              {new Date(comment.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{comment.text}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 italic">{t('waivers.noComments')}</p>
                    )}
                  </div>

                  {}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={t('waivers.addCommentPlaceholder')}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || isSubmitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {}
              <div className="space-y-6">
                {}
                {(waiver.status === 'under_voting' || waiver.status === 'approved' || waiver.status === 'rejected') && (
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <h3 className="font-semibold text-slate-800 mb-4">{t('waivers.votingStatus')}</h3>

                    {}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-slate-600">{t('waivers.approval')}</span>
                        <span className={`font-bold ${(waiver.currentApproval || 0) >= (waiver.requiredMajority || 66.67) ? 'text-green-600' : 'text-blue-600'}`}>
                          {(waiver.currentApproval || 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${(waiver.currentApproval || 0) >= (waiver.requiredMajority || 66.67) ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${waiver.currentApproval || 0}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-slate-500">{t('waivers.requiredMajority')}: {waiver.requiredMajority || 66.67}%</span>
                      </div>
                    </div>

                    {}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-800">{waiver.votesReceived || 0}</p>
                        <p className="text-xs text-slate-500">{t('waivers.received')}</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{waiver.votesFor || 0}</p>
                        <p className="text-xs text-green-600">{t('waivers.for')}</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{waiver.votesAgainst || 0}</p>
                        <p className="text-xs text-red-600">{t('waivers.against')}</p>
                      </div>
                    </div>

                    {}
                    <p className="text-sm text-slate-500 mb-4">
                      {(waiver.totalLenders || 0) - (waiver.votesReceived || 0)} {t('waivers.pendingVotes')}
                    </p>

                    {}
                    {waiver.status === 'under_voting' && userRole === ROLES.LENDER && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => onVote(waiver.dbId, 'approve')}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <ThumbsUp size={18} />
                          {t('waivers.approve')}
                        </button>
                        <button
                          onClick={() => onVote(waiver.dbId, 'reject')}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          <ThumbsDown size={18} />
                          {t('waivers.reject')}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {}
                {waiver.status === 'pending_review' && userRole === ROLES.AGENT && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <h3 className="font-semibold text-amber-800 mb-3">{t('waivers.agentAction')}</h3>
                    <p className="text-sm text-amber-700 mb-4">{t('waivers.agentReviewMessage')}</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => onApproveForVoting(waiver.dbId)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Users size={18} />
                        {t('waivers.openForVoting')}
                      </button>
                      <button className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100">
                        <MessageSquare size={18} />
                        {t('waivers.requestMoreInfo')}
                      </button>
                    </div>
                  </div>
                )}

                {}
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <History size={18} />
                    {t('waivers.timeline')}
                  </h3>
                  <div className="space-y-4">
                    {waiver.timeline && waiver.timeline.length > 0 ? (
                      waiver.timeline.map((event, idx) => (
                        <div key={idx} className="flex gap-3">
                          <div className={`w-2 h-2 mt-2 rounded-full ${
                            event.event_type === 'approved' ? 'bg-green-500' :
                            event.event_type === 'rejected' ? 'bg-red-500' :
                            event.event_type === 'voting_opened' ? 'bg-blue-500' :
                            'bg-amber-500'
                          }`} />
                          <div>
                            <p className="text-sm font-medium text-slate-800">{event.event_title}</p>
                            <p className="text-xs text-slate-500">{new Date(event.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <>
                        <div className="flex gap-3">
                          <div className="w-2 h-2 mt-2 bg-blue-500 rounded-full" />
                          <div>
                            <p className="text-sm font-medium text-slate-800">{t('waivers.requestCreated')}</p>
                            <p className="text-xs text-slate-500">{waiver.requestDate ? new Date(waiver.requestDate).toLocaleDateString() : '-'}</p>
                          </div>
                        </div>
                        {waiver.votingDeadline && (
                          <div className="flex gap-3">
                            <div className="w-2 h-2 mt-2 bg-amber-500 rounded-full" />
                            <div>
                              <p className="text-sm font-medium text-slate-800">{t('waivers.votingDeadline')}</p>
                              <p className="text-xs text-slate-500">{new Date(waiver.votingDeadline).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )}
                        {waiver.approvalDate && (
                          <div className="flex gap-3">
                            <div className="w-2 h-2 mt-2 bg-green-500 rounded-full" />
                            <div>
                              <p className="text-sm font-medium text-slate-800">{t('waivers.approved')}</p>
                              <p className="text-xs text-slate-500">{new Date(waiver.approvalDate).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )}
                        {waiver.rejectionDate && (
                          <div className="flex gap-3">
                            <div className="w-2 h-2 mt-2 bg-red-500 rounded-full" />
                            <div>
                              <p className="text-sm font-medium text-slate-800">{t('waivers.rejected')}</p>
                              <p className="text-xs text-slate-500">{new Date(waiver.rejectionDate).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {}
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">{t('waivers.requestInfo')}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('waivers.requestedBy')}</span>
                      <span className="text-slate-800">{waiver.requestedBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('waivers.totalLenders')}</span>
                      <span className="text-slate-800">{waiver.totalLenders || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


function Waivers() {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const { loans } = useLoans();


  const userContext = useMemo(() => ({
    role: user?.role,
    userId: user?.id
  }), [user?.role, user?.id]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewWaiverModal, setShowNewWaiverModal] = useState(false);
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const { waivers, loading, refetch } = useWaivers(
    { status: statusFilter !== 'all' ? statusFilter : undefined, search: searchQuery },
    userContext
  );
  const { stats } = useWaiverStats(userContext);
  const {
    createWaiver,
    submitForReview,
    openForVoting,
    castVote,
    addComment,
    addDocument,
    addNegotiationProposal,
    acceptNegotiationProposal
  } = useWaiverMutations();


  const filteredWaivers = useMemo(() => {
    let result = [...waivers];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(w =>
        (w.borrower || '').toLowerCase().includes(query) ||
        (w.covenantName || '').toLowerCase().includes(query) ||
        (w.id || '').toLowerCase().includes(query)
      );
    }
    return result;
  }, [waivers, searchQuery]);


  const handleCreateWaiver = useCallback(async (formData, documents) => {
    setIsSubmitting(true);
    try {
      const waiverData = {
        ...formData,
        loanId: formData.loanId
      };
      console.log('Creating waiver with data:', waiverData, 'userId:', user?.id, 'userName:', user?.name);
      const result = await createWaiver(waiverData, user?.id, user?.name);
      console.log('Create waiver result:', result);
      if (result.success) {

        const waiverId = result.data.dbId || result.data.id;
        console.log('Waiver created with dbId:', waiverId, 'result.data:', result.data);
        if (documents.length > 0) {
          for (const doc of documents) {
            console.log('Adding document:', doc.name, 'to waiver:', waiverId);
            const docResult = await addDocument(waiverId, { name: doc.name }, user?.id);
            console.log('Add document result:', docResult);
          }
        }
        setShowNewWaiverModal(false);
        refetch();
      } else {
        console.error('Failed to create waiver:', result.error);
        alert('Erreur lors de la création: ' + (result.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Error creating waiver:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [createWaiver, addDocument, user?.id, user?.name, refetch]);


  const handleVote = useCallback(async (waiverId, vote) => {
    try {
      await castVote(waiverId, vote, user?.id, user?.name, null, null);
      refetch();
      if (selectedWaiver && selectedWaiver.dbId === waiverId) {
        setSelectedWaiver(null);
      }
    } catch (error) {
      console.error('Vote error:', error);
    }
  }, [castVote, user?.id, user?.name, refetch, selectedWaiver]);


  const handleApproveForVoting = useCallback(async (waiverId) => {
    try {
      await openForVoting(waiverId, user?.id, user?.name);
      refetch();
      setSelectedWaiver(null);
    } catch (error) {
      console.error('Open for voting error:', error);
    }
  }, [openForVoting, user?.id, user?.name, refetch]);


  const handleAddComment = useCallback(async (waiverId, text) => {
    try {
      await addComment(waiverId, text, user?.id, user?.name, user?.role);
      refetch();

      if (selectedWaiver) {
        const updated = waivers.find(w => w.dbId === waiverId);
        if (updated) {
          setSelectedWaiver({ ...updated });
        }
      }
    } catch (error) {
      console.error('Add comment error:', error);
    }
  }, [addComment, user?.id, user?.name, user?.role, refetch, selectedWaiver, waivers]);


  const handleMakeCounterProposal = useCallback(async (proposalData) => {
    if (!selectedWaiver) return;
    try {
      await addNegotiationProposal(
        selectedWaiver.dbId,
        proposalData,
        user?.id,
        user?.name,
        user?.role
      );
      refetch();
    } catch (error) {
      console.error('Counter proposal error:', error);
    }
  }, [addNegotiationProposal, selectedWaiver, user?.id, user?.name, user?.role, refetch]);


  const handleAcceptProposal = useCallback(async (proposalId) => {
    if (!selectedWaiver) return;
    try {
      await acceptNegotiationProposal(selectedWaiver.dbId, proposalId, user?.id, user?.name);
      refetch();
      setSelectedWaiver(null);
    } catch (error) {
      console.error('Accept proposal error:', error);
    }
  }, [acceptNegotiationProposal, selectedWaiver, user?.id, user?.name, refetch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('waivers.title')}</h2>
          <p className="text-slate-500">{t('waivers.subtitle')}</p>
        </div>
        {(hasRole(ROLES.BORROWER) || hasRole(ROLES.AGENT)) && (
          <button
            onClick={() => setShowNewWaiverModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={18} />
            {t('waivers.newRequest')}
          </button>
        )}
      </div>

      {}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg shrink-0">
              <FileText size={16} className="sm:w-5 sm:h-5 text-slate-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-slate-800">{stats?.total || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{t('waivers.totalRequests')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg shrink-0">
              <Clock size={16} className="sm:w-5 sm:h-5 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats?.pending || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{t('waivers.pendingReview')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg shrink-0">
              <Users size={16} className="sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats?.voting || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{t('waivers.underVoting')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg shrink-0">
              <CheckCircle size={16} className="sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-green-600">{stats?.approved || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{t('waivers.approved')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 col-span-2 sm:col-span-1">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg shrink-0">
              <XCircle size={16} className="sm:w-5 sm:h-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-red-600">{stats?.rejected || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{t('waivers.rejected')}</p>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={t('waivers.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('waivers.allStatus')}</option>
            <option value="draft">{t('waivers.status.draft')}</option>
            <option value="pending_review">{t('waivers.status.pending_review')}</option>
            <option value="negotiating">{t('waivers.status.negotiating')}</option>
            <option value="under_voting">{t('waivers.status.under_voting')}</option>
            <option value="approved">{t('waivers.status.approved')}</option>
            <option value="rejected">{t('waivers.status.rejected')}</option>
          </select>
        </div>
      </div>

      {}
      {filteredWaivers.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredWaivers.map(waiver => (
            <WaiverCard
              key={waiver.dbId || waiver.id}
              waiver={waiver}
              userRole={user?.role}
              onView={setSelectedWaiver}
              onVote={handleVote}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-800 mb-2">{t('waivers.noWaivers')}</h3>
          <p className="text-slate-500">{t('waivers.noWaiversDescription')}</p>
        </div>
      )}

      {}
      <NewWaiverModal
        isOpen={showNewWaiverModal}
        onClose={() => setShowNewWaiverModal(false)}
        loans={loans}
        onSubmit={handleCreateWaiver}
        isSubmitting={isSubmitting}
      />

      <WaiverDetailModal
        waiver={selectedWaiver}
        isOpen={!!selectedWaiver}
        onClose={() => setSelectedWaiver(null)}
        userRole={user?.role}
        user={user}
        onVote={handleVote}
        onApproveForVoting={handleApproveForVoting}
        onAddComment={handleAddComment}
        onMakeCounterProposal={handleMakeCounterProposal}
        onAcceptProposal={handleAcceptProposal}
      />
    </div>
  );
}

export default Waivers;